/**
 * Amazon Orders API returns Pending, Unshipped, PartiallyShipped, Shipped, Canceled.
 * Delivered is inferred when:
 * - ITL track says Delivered (I2I / ITL-booked packets), or
 * - Amazon marks Shipped and expected delivery date has passed (non-ITL only), or
 * - ops click Mark delivered for early SC delivery.
 */

function hasItlWaybill(order) {
  return Boolean(String(order?.i2iTrackingId || '').trim());
}

export function resolveItlDisplayStatus(order) {
  const raw = String(order?.itlStatus || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  let tone = 'info';
  if (lower === 'delivered' || lower.includes('delivered')) tone = 'success';
  else if (lower.includes('cancel')) tone = 'danger';
  else if (lower.includes('rto') || lower.includes('undelivered') || lower.includes('lost')) {
    tone = 'warning';
  }
  return {
    key: `itl:${lower}`,
    label: raw,
    tone,
    hint: order?.itlLastScanRemark
      ? `ITL: ${order.itlLastScanRemark}`
      : `ITL tracking status${order?.itlStatusCode ? ` (${order.itlStatusCode})` : ''}.`,
  };
}

export function resolveOrderDisplayStatus(order, now = Date.now()) {
  const status = String(order?.status || 'pending').toLowerCase();
  const itlBooked = hasItlWaybill(order);
  const itlDisplay = itlBooked ? resolveItlDisplayStatus(order) : null;

  if (status === 'cancelled' || status === 'canceled') {
    return { key: 'cancelled', label: 'Cancelled', tone: 'danger', hint: 'Order was cancelled on Amazon.' };
  }

  if (status === 'returned') {
    return {
      key: 'returned',
      label: itlDisplay?.label?.toLowerCase().includes('rto') ? itlDisplay.label : 'Returned',
      tone: 'warning',
      hint: itlDisplay?.hint || 'Buyer return / RTO recorded.',
    };
  }

  if (status === 'delivered') {
    return {
      key: 'delivered',
      label: 'Delivered',
      tone: 'success',
      hint: itlBooked
        ? itlDisplay?.hint || 'Confirmed delivered via ITL tracking.'
        : order?.deliveryDate
          ? `Expected delivery was ${new Date(order.deliveryDate).toLocaleDateString('en-IN')}.`
          : 'Marked delivered.',
    };
  }

  // ITL-booked: show live courier status while CRM order is still shipped.
  if (itlBooked && status === 'shipped' && itlDisplay) {
    return itlDisplay;
  }

  const deliveryMs = order?.deliveryDate ? new Date(order.deliveryDate).getTime() : NaN;
  const isPastDelivery = Number.isFinite(deliveryMs) && deliveryMs < now;

  if (!itlBooked && status === 'shipped' && isPastDelivery) {
    return {
      key: 'delivered',
      label: 'Delivered',
      tone: 'success',
      hint: order?.deliveryDate
        ? `Expected delivery was ${new Date(order.deliveryDate).toLocaleDateString('en-IN')}.`
        : 'Delivery window has passed.',
    };
  }

  if (status === 'shipped') {
    return {
      key: 'shipped',
      label: itlBooked ? 'In transit' : 'Shipped',
      tone: 'info',
      hint: itlBooked
        ? order?.itlExpectedDelivery
          ? `ITL — expected by ${order.itlExpectedDelivery}.`
          : 'ITL booked — waiting for courier scans.'
        : order?.deliveryDate
          ? `In transit — expected by ${new Date(order.deliveryDate).toLocaleDateString('en-IN')}.`
          : 'Marked shipped on Amazon.',
    };
  }

  if (status === 'unshipped' || status === 'partiallyshipped') {
    return {
      key: 'unshipped',
      label: 'Unshipped',
      tone: 'warning',
      hint: 'Waiting to ship — see Unshipped tab.',
    };
  }

  return {
    key: 'pending',
    label: 'Pending',
    tone: 'warning',
    hint: 'Order confirmed but not yet due to ship.',
  };
}

/** Unit count for Seller Central–style "Unshipped (2)" badges. */
export function getOrderUnitCount(order) {
  const items = order?.items || [];
  if (items.length) {
    return items.reduce((sum, item) => sum + (Number(item.quantityOrdered) || 0), 0);
  }
  const fallback = Number(order?.itemCount);
  return Number.isFinite(fallback) && fallback > 0 ? fallback : 1;
}

/**
 * Solid red Amazon Seller Central–style status chip: "Unshipped (1)".
 */
export function AmazonUnshippedBadge({ order, now }) {
  const display = resolveOrderDisplayStatus(order, now);
  const units = getOrderUnitCount(order);

  if (display.key === 'cancelled') {
    return (
      <span className="inline-flex items-center rounded-sm bg-[#565959] px-2 py-0.5 text-[12px] font-bold leading-5 text-white">
        Cancelled ({units})
      </span>
    );
  }

  if (display.key === 'pending') {
    return (
      <span className="inline-flex items-center rounded-sm bg-[#c45500] px-2 py-0.5 text-[12px] font-bold leading-5 text-white">
        Pending ({units})
      </span>
    );
  }

  return (
    <span
      title={display.hint}
      className="inline-flex items-center rounded-sm bg-[#cc0c39] px-2 py-0.5 text-[12px] font-bold leading-5 text-white shadow-sm"
    >
      Unshipped ({units})
    </span>
  );
}

export function OrderStatusBadge({ order, now }) {
  const display = resolveOrderDisplayStatus(order, now);

  return (
    <span title={display.hint} className="inline-flex">
      <OrderStatusBadgeInner tone={display.tone} label={display.label} />
    </span>
  );
}

function OrderStatusBadgeInner({ tone, label }) {
  const dotColors = {
    default: 'bg-slate-400',
    success: 'bg-emerald-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
    danger: 'bg-red-500',
  };

  const textColors = {
    default: 'text-slate-600',
    success: 'text-emerald-800',
    warning: 'text-amber-800',
    info: 'text-blue-800',
    danger: 'text-red-800',
  };

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
      <span className={`h-2 w-2 rounded-full ${dotColors[tone] || dotColors.default}`} />
      <span className={textColors[tone] || textColors.default}>{label}</span>
    </span>
  );
}
